import { Select2OptionData } from '@components/shared/select2/select2.interface';
import {
  SCENARIO_TYPE_HISTORICAL,
  SCENARIO_TYPE_PROJECTION,
  SCENARIO_TYPE_PROFORMA,
  SCENARIO_TYPE_OPTIONS,
  NON_HISTORICAL_SCENARIO_TYPE_OPTIONS,
} from '@utils/constants';

/**
 * The scenario type options a user may pick from, given the scenarios of the
 * statements feeding the build.
 *
 * Once any non-historical input is involved, the result can no longer be a
 * Historical scenario, so Historical is dropped from the offered options.
 *
 * @param inputScenarios The distinct scenarios of the selected input periods.
 * @returns The scenario type options to offer.
 */
export function availableScenarioTypes(inputScenarios: Set<string>): Select2OptionData[] {
  const hasNonHistorical = [...inputScenarios].some(scenario => scenario !== SCENARIO_TYPE_HISTORICAL);
  return hasNonHistorical ? NON_HISTORICAL_SCENARIO_TYPE_OPTIONS : SCENARIO_TYPE_OPTIONS;
}

/**
 * The scenario type to default to for a given set of input scenarios.
 *
 * Projection takes precedence over Proforma when both are present; an
 * all-historical (or empty) input set defaults to Historical.
 *
 * @param inputScenarios The distinct scenarios of the selected input periods.
 * @returns The derived default scenario type.
 */
export function defaultScenarioType(inputScenarios: Set<string>): string {
  if (inputScenarios.has(SCENARIO_TYPE_PROJECTION)) { return SCENARIO_TYPE_PROJECTION; }
  if (inputScenarios.has(SCENARIO_TYPE_PROFORMA)) { return SCENARIO_TYPE_PROFORMA; }
  return SCENARIO_TYPE_HISTORICAL;
}

/**
 * Resolve the scenario type to use, keeping the user's explicit choice only
 * while it remains valid for the current inputs.
 *
 * An unlocked (not user-set) value is always re-derived from the inputs. A
 * locked value is preserved unless it is no longer among the available
 * options — for example, a previously-chosen Historical type once a Projection
 * input is added — in which case it is re-defaulted.
 *
 * @param current The current scenario type.
 * @param isLocked Whether the user explicitly chose the current value.
 * @param inputScenarios The distinct scenarios of the selected input periods.
 * @returns The scenario type to apply.
 */
export function resolveScenarioType(current: string, isLocked: boolean, inputScenarios: Set<string>): string {
  const availableIds = availableScenarioTypes(inputScenarios).map(option => option.id);
  return (!isLocked || !availableIds.includes(current))
    ? defaultScenarioType(inputScenarios)
    : current;
}
